import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { toast } from 'sonner';
import { Loader2, RefreshCw, Users, ShoppingBag, AlertOctagon, ScrollText, Trash2, BookOpen, ShieldAlert, Activity } from 'lucide-react';
import { getApiBase } from '../../api/httpClient';
import { diffAdIdSets } from '../../utils/canonicalAdFilter';
import './AdminLayout.css';

const STATUS_LABELS_EN = {
  draft: 'Draft',
  pending_review: 'Pending Review',
  active: 'Active',
  hidden_by_user: 'Hidden by user',
  sold: 'Sold',
  rejected: 'Rejected',
  hidden_by_reports: 'Hidden (reports)',
  removed_by_moderation: 'Removed',
  needs_photo_reupload: 'Needs Photo Reupload',
};
const STATUS_LABELS_AR = {
  draft: 'مسودة',
  pending_review: 'قيد المراجعة',
  active: 'نشط',
  hidden_by_user: 'مخفي بواسطة المالك',
  sold: 'مباع',
  rejected: 'مرفوض',
  hidden_by_reports: 'مخفي (بلاغات)',
  removed_by_moderation: 'مزال',
  needs_photo_reupload: 'بحاجة لإعادة رفع الصورة',
};

const STATUS_PILL = {
  active: 'green',
  pending_review: 'yellow',
  rejected: 'red',
  removed_by_moderation: 'red',
  hidden_by_reports: 'red',
  hidden_by_user: 'gray',
  sold: 'blue',
  draft: 'gray',
  // iter278 — neutral cleanup state.
  needs_photo_reupload: 'yellow',
};

// iter325 — Module-level WhyChip so React keeps it stable across
// renders (no `react/no-unstable-nested-components` warning). Each
// AdminOverview KPI renders one of these with the canonical query
// + included/excluded statuses + source endpoint so an operator
// can audit a number directly from the phone.
function WhyChip({ k, query, includes = [], excludes = [], source, isOpen, onToggle, isArabic, lastRefreshHuman }) {
  return (
    <span style={{ position: 'relative', display: 'inline-flex', marginInlineStart: 6 }}>
      <button
        type="button"
        className="admin-pill gray"
        data-testid={`why-${k}`}
        data-open={String(isOpen)}
        onClick={(e) => { e.stopPropagation(); onToggle(); }}
        style={{ cursor: 'pointer', fontSize: 10, padding: '1px 6px', lineHeight: 1.4 }}
        aria-expanded={isOpen}
        aria-label={isArabic ? 'لماذا هذا الرقم؟' : 'Why this number?'}
      >
        ?
      </button>
      {isOpen && (
        <div
          data-testid={`why-${k}-popover`}
          className="admin-card"
          style={{
            position: 'absolute', top: '120%', insetInlineStart: 0, zIndex: 50,
            minWidth: 260, maxWidth: 340, padding: 12, fontSize: 12,
            boxShadow: '0 10px 30px rgba(0,0,0,0.18)',
            background: 'var(--admin-card-bg, #fff)',
            border: '1px solid var(--admin-border, #e5e7eb)',
            borderRadius: 8,
            direction: isArabic ? 'rtl' : 'ltr',
            textAlign: isArabic ? 'right' : 'left',
          }}
          role="dialog"
        >
          <div style={{ fontWeight: 700, marginBottom: 6 }}>
            {isArabic ? 'لماذا هذا الرقم؟' : 'Why this number?'}
          </div>
          {query && (
            <div style={{ marginBottom: 4 }}>
              <strong>{isArabic ? 'الاستعلام:' : 'Query:'}</strong>{' '}
              <code style={{ fontSize: 11 }}>{query}</code>
            </div>
          )}
          {includes.length > 0 && (
            <div style={{ marginBottom: 4 }}>
              <strong>{isArabic ? 'الحالات المضمّنة:' : 'Included:'}</strong> {includes.join(', ')}
            </div>
          )}
          {excludes.length > 0 && (
            <div style={{ marginBottom: 4 }}>
              <strong>{isArabic ? 'مستثناة:' : 'Excluded:'}</strong> {excludes.join(', ')}
            </div>
          )}
          {source && (
            <div style={{ marginBottom: 4 }}>
              <strong>{isArabic ? 'المصدر:' : 'Source:'}</strong>{' '}
              <code style={{ fontSize: 11 }}>{source}</code>
            </div>
          )}
          <div style={{ fontSize: 11, opacity: 0.75, marginTop: 6 }}>
            {isArabic ? 'آخر تحديث:' : 'Last refresh:'} {lastRefreshHuman}
          </div>
        </div>
      )}
    </span>
  );
}

// iter325 — Counts-Verified chip. Reads the /api/admin/health/counts-verify
// payload and renders a green "Counts verified" chip when everything
// agrees, or an amber "Count mismatch" chip listing exactly which
// pair diverges. Stable module-level component so React skips the
// unnecessary re-mount on every parent render.
function CountsChip({ countsHealth, isArabic }) {
  if (!countsHealth) return null;
  const verified = countsHealth.overall === 'verified';
  return (
    <div
      data-testid="counts-verified-chip"
      data-overall={countsHealth.overall}
      style={{
        marginTop: 8, display: 'inline-flex', alignItems: 'center', gap: 8,
        padding: '6px 10px', borderRadius: 999,
        background: verified ? '#dcfce7' : '#fef3c7',
        color: verified ? '#166534' : '#92400e',
        fontSize: 12, fontWeight: 600,
      }}
      role="status"
    >
      <span aria-hidden="true">{verified ? '✓' : '⚠'}</span>
      <span>
        {verified
          ? (isArabic ? 'الأرقام مطابقة' : 'Counts verified')
          : (isArabic ? 'عدم تطابق في الأرقام' : 'Count mismatch')}
      </span>
      {!verified && Array.isArray(countsHealth.mismatches) && countsHealth.mismatches.length > 0 && (
        <span style={{ fontWeight: 400, opacity: 0.85, fontSize: 11 }}>
          ({countsHealth.mismatches.join(' · ')})
        </span>
      )}
    </div>
  );
}

export default function AdminOverview() {
  const { token, user } = useAuth();
  const { lang } = useLanguage();
  const navigate = useNavigate();
  const isArabic = lang === 'ar';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // iter320 — "Live Shop Ads" cross-checker. Holds the canonical
  // ad_id list from `/api/admin/stats/live-ads-debug` and the
  // ad_id list returned by `/api/ads` (what Home / Shop actually
  // render). A non-empty `diff` means the operator should
  // investigate; the panel is always rendered when present.
  const [debugSnapshot, setDebugSnapshot] = useState(null);
  const [syncing, setSyncing] = useState(false);
  // iter321 — Per-ad metadata for the mobile mismatch diagnostic.
  // Holds the response of `/api/admin/ads/inspect`. The operator
  // can read it directly on the phone — no DevTools required.
  const [mismatchInspect, setMismatchInspect] = useState(null);
  const [inspecting, setInspecting] = useState(false);
  // iter325 — Counts-verified chip data. Re-checked on every load /
  // refresh / sync. `overall: 'verified'` paints the chip green;
  // anything else paints it amber and exposes which modules disagree
  // so the operator does not have to dig through DevTools.
  const [countsHealth, setCountsHealth] = useState(null);
  // iter325 — Data Health / Debug section collapsed by default
  // because the operator asked for it to be hidden from primary view.
  const [debugOpen, setDebugOpen] = useState(false);
  // iter325 — Which "Why this number?" popover is currently open. Only
  // one at a time — tap again to close. The popover renders the query
  // / included statuses / excluded statuses / source endpoint so the
  // operator can audit the number from the phone (no DevTools).
  const [openWhyKey, setOpenWhyKey] = useState(null);

  // Owner-only entry to the cleanup tools.
  const isOwner = (
    user?.is_admin &&
    (user?.email || '').trim().toLowerCase() === 'wassla.connect@gmail.com'
  );

  // KPI deep-links — Stage B: Users / Books / Reports / Banned now jump
  // to the premium admin shell pages (no more legacy /admin/moderation
  // fallback). Pending Review stays on /admin/ads with pre-filter.
  const goToUsers = useCallback(
    () => navigate('/admin/users'),
    [navigate]
  );
  const goToReports = useCallback(
    () => navigate('/admin/reports'),
    [navigate]
  );
  const goToBanned = useCallback(
    () => navigate('/admin/users?filter=banned'),
    [navigate]
  );
  const goToPendingBan = useCallback(
    () => navigate('/admin/users?filter=pending_ban'),
    [navigate]
  );
  const goToSystemHealth = useCallback(
    () => navigate('/admin/system-health'),
    [navigate]
  );
  const goToPendingAds = useCallback(
    () => navigate('/admin/ads?status=pending_review'),
    [navigate]
  );
  const goToShopAds = useCallback(
    () => navigate('/admin/ads?status=active'),
    [navigate]
  );
  const goToRejectedAds = useCallback(
    () => navigate('/admin/ads?status=rejected'),
    [navigate]
  );
  const goToAdsByStatus = useCallback(
    (s) => navigate(`/admin/ads?status=${s}`),
    [navigate]
  );
  const goToBooks = useCallback(
    () => navigate('/admin/books'),
    [navigate]
  );
  const goToBooksByStatus = useCallback(
    (s) => navigate(`/admin/books?status=${s}`),
    [navigate]
  );

  const load = useCallback(async () => {
    // Wait until AuthContext has hydrated a token. If the Bearer is missing
    // we'd otherwise send the literal string "Bearer null" and the backend
    // would return 401 "Not authenticated" — exactly the symptom we saw.
    const rawToken = token || (typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null);
    const liveToken =
      rawToken && rawToken !== 'null' && rawToken !== 'undefined'
        ? String(rawToken).trim()
        : null;
    if (!liveToken) {
      setError({
        endpoint: '/api/admin/dashboard/overview',
        status: 401,
        message: isArabic
          ? 'انتهت الجلسة. الرجاء تسجيل الدخول مرة أخرى.'
          : 'Your session has expired. Please sign in again.',
        actionable: 'login',
      });
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    // Smart base: on production custom domains (e.g. wassla.tech), use
    // same-origin relative URLs to avoid CORS / "failed to fetch" caused by
    // a stale cross-origin REACT_APP_BACKEND_URL baked into the build.
    const apiBase = getApiBase();
    const path = '/api/admin/dashboard/overview';
    // iter320 — Cache-busting query param + `cache: 'no-store'`.
    // Production wassla.tech / CDN previously could return a 60-s
    // stale dashboard JSON and the operator would see a count
    // that lagged behind the live Shop page. The bust is on every
    // fetch (not just Sync) so any in-app navigation back to the
    // overview always shows a fresh number.
    const endpoint = `${apiBase}${path}?_t=${Date.now()}`;
    try {
      const resp = await fetch(endpoint, {
        headers: { Authorization: `Bearer ${liveToken}`, 'Cache-Control': 'no-cache' },
        credentials: 'include',
        cache: 'no-store',
      });
      if (!resp.ok) {
        let bodyMsg = '';
        try {
          const j = await resp.json();
          bodyMsg = j?.detail || JSON.stringify(j).slice(0, 200);
        } catch (_e) {
          bodyMsg = (await resp.text()).slice(0, 200);
        }
        const actionable =
          resp.status === 401 ? 'login' :
          resp.status === 403 ? 'no_admin' : null;
        setError({
          endpoint: path,
          status: resp.status,
          message: bodyMsg || resp.statusText || 'No detail',
          actionable,
        });
        toast.error(
          isArabic ? `فشل ${resp.status}: ${bodyMsg}` : `Failed ${resp.status}: ${bodyMsg}`
        );
        return;
      }
      const json = await resp.json();
      setData(json);
      // iter325 — Fire-and-forget counts-verify check so the chip
      // updates without blocking the main dashboard render. A
      // failure here is non-fatal — we just hide the chip until
      // the next refresh.
      try {
        const verifyResp = await fetch(
          `${apiBase}/api/admin/health/counts-verify?_t=${Date.now()}`,
          {
            headers: { Authorization: `Bearer ${liveToken}`, 'Cache-Control': 'no-cache' },
            credentials: 'include',
            cache: 'no-store',
          }
        );
        if (verifyResp.ok) {
          setCountsHealth(await verifyResp.json());
        } else {
          setCountsHealth(null);
        }
      } catch (_e) {
        setCountsHealth(null);
      }
    } catch (e) {
      setError({
        endpoint: path,
        status: 0,
        message: (e && e.message) ? e.message : String(e),
        actionable: null,
      });
      toast.error(isArabic ? `تعذر الاتصال: ${e?.message || ''}` : `Network error: ${e?.message || ''}`);
    } finally {
      setLoading(false);
    }
  }, [token, isArabic]);

  // Re-run when AuthContext hydrates the token AFTER initial mount
  // (handles the race where AdminOverview mounts before /api/auth/me returns).
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (token && error && error.actionable === 'login') {
      load();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // iter320 — "Sync now" — re-fetch the dashboard AND pull the
  // canonical visible-ad_ids list + the public /api/ads payload so
  // we can compute the symmetric-difference and surface any drift
  // the operator would otherwise have to count manually on the
  // Shop grid. Use no-store + cache-busters everywhere.
  const syncNow = useCallback(async () => {
    if (syncing) return;
    const rawToken = token || (typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null);
    const liveToken = rawToken && rawToken !== 'null' && rawToken !== 'undefined' ? String(rawToken).trim() : null;
    if (!liveToken) {
      toast.error(isArabic ? 'انتهت الجلسة' : 'Session expired');
      return;
    }
    setSyncing(true);
    try {
      const apiBase = getApiBase();
      const ts = Date.now();
      const [overviewRes, debugRes, publicRes] = await Promise.all([
        fetch(`${apiBase}/api/admin/dashboard/overview?_t=${ts}`, {
          headers: { Authorization: `Bearer ${liveToken}`, 'Cache-Control': 'no-cache' },
          credentials: 'include',
          cache: 'no-store',
        }),
        fetch(`${apiBase}/api/admin/stats/live-ads-debug?_t=${ts}`, {
          headers: { Authorization: `Bearer ${liveToken}`, 'Cache-Control': 'no-cache' },
          credentials: 'include',
          cache: 'no-store',
        }),
        fetch(`${apiBase}/api/ads?limit=500&_t=${ts}`, {
          headers: { 'Cache-Control': 'no-cache' },
          cache: 'no-store',
        }),
      ]);
      if (!overviewRes.ok || !debugRes.ok || !publicRes.ok) {
        toast.error(isArabic ? 'فشل التحديث' : 'Sync failed');
        return;
      }
      const overviewJson = await overviewRes.json();
      const debugJson = await debugRes.json();
      const publicJson = await publicRes.json();
      setData(overviewJson);
      const canonicalIds = Array.isArray(debugJson?.ad_ids) ? debugJson.ad_ids : [];
      const publicIds = Array.isArray(publicJson?.ads)
        ? publicJson.ads.map(a => a.ad_id).filter(Boolean)
        : [];
      const diff = diffAdIdSets(canonicalIds, publicIds);
      const snapshot = {
        adminLiveCount: overviewJson?.ads?.public_live ?? overviewJson?.ads?.shop_visible ?? null,
        canonicalCount: debugJson?.count ?? canonicalIds.length,
        publicAdsCount: typeof publicJson?.total === 'number' ? publicJson.total : publicIds.length,
        canonicalIds,
        publicIds,
        diff,
        serverTime: debugJson?.server_time || null,
        fetchedAt: new Date().toISOString(),
      };
      setDebugSnapshot(snapshot);
      // Dev-only mismatch detector — log to console so the operator
      // can capture the diff in production (DevTools opens on
      // wassla.tech). NODE_ENV is "production" inside the deployed
      // bundle, so we ALSO log when there's an actual mismatch
      // (never noisy when counts agree).
      const counts = [snapshot.adminLiveCount, snapshot.canonicalCount, snapshot.publicAdsCount].filter(v => typeof v === 'number');
      const mismatched = new Set(counts).size > 1;
      if (typeof process !== 'undefined' && process.env && process.env.NODE_ENV === 'development') {
        console.debug('[AdminOverview/syncNow]', snapshot);
      }
      if (mismatched) {
        console.warn('Admin live ad count mismatch', {
          adminLiveShopAds: snapshot.adminLiveCount,
          canonicalLiveAdsDebug: snapshot.canonicalCount,
          publicAdsTotal: snapshot.publicAdsCount,
          inCanonicalNotPublic: diff.inAOnly,
          inPublicNotCanonical: diff.inBOnly,
          serverTime: snapshot.serverTime,
        });
        toast.warning(
          isArabic
            ? 'عدم تطابق في عدد الإعلانات. راجع لوحة التشخيص.'
            : 'Admin live ad count mismatch — see debug panel.'
        );
        // iter321 — Fetch per-ad metadata for the mismatch table so
        // the operator can read the diagnostic directly on a phone
        // (no DevTools). Union of both diff directions, max 100.
        const probeIds = [...new Set([...diff.inAOnly, ...diff.inBOnly])].slice(0, 100);
        if (probeIds.length > 0) {
          setInspecting(true);
          try {
            const inspectRes = await fetch(`${apiBase}/api/admin/ads/inspect?_t=${ts}`, {
              method: 'POST',
              headers: {
                Authorization: `Bearer ${liveToken}`,
                'Content-Type': 'application/json',
                'Cache-Control': 'no-cache',
              },
              credentials: 'include',
              cache: 'no-store',
              body: JSON.stringify({ ad_ids: probeIds }),
            });
            if (inspectRes.ok) {
              const inspectJson = await inspectRes.json();
              setMismatchInspect(Array.isArray(inspectJson?.items) ? inspectJson.items : []);
            } else {
              setMismatchInspect([]);
            }
          } catch (e) {
            setMismatchInspect([]);
          } finally {
            setInspecting(false);
          }
        } else {
          setMismatchInspect([]);
        }
      } else {
        toast.success(isArabic ? 'تم تحديث الإحصاءات' : 'Stats synced');
        // Clear any prior mismatch inspect so the panel doesn't
        // linger from an earlier divergent state.
        setMismatchInspect(null);
      }
    } catch (e) {
      toast.error(isArabic ? `خطأ: ${e?.message || ''}` : `Error: ${e?.message || ''}`);
    } finally {
      setSyncing(false);
    }
  }, [token, isArabic, syncing]);

  // Pre-compute the mismatch flag once per render so the helper
  // text + chip stay in sync.
  const mismatchFlag = useMemo(() => {
    if (!debugSnapshot) return false;
    const counts = [debugSnapshot.adminLiveCount, debugSnapshot.canonicalCount, debugSnapshot.publicAdsCount].filter(v => typeof v === 'number');
    return new Set(counts).size > 1;
  }, [debugSnapshot]);

  if (loading && !data && !error) {
    return (
      <div className="admin-card" data-testid="admin-overview-loading">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Loader2 className="animate-spin" size={18} />
          <span>{isArabic ? 'جارٍ التحميل…' : 'Loading…'}</span>
        </div>
      </div>
    );
  }

  // Visible diagnostic error card — no more silent "blank body" state.
  if (error && !data) {
    return (
      <div data-testid="admin-overview">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <div>
            <h1 style={{ margin: 0, fontSize: 18 }}>
              {isArabic ? 'نظرة عامة على المنصة' : 'Platform Overview'}
            </h1>
          </div>
          <button
            type="button"
            className="admin-btn"
            onClick={load}
            data-testid="admin-overview-refresh"
          >
            <RefreshCw size={14} />
            {isArabic ? 'تحديث' : 'Refresh'}
          </button>
        </div>

        {isOwner && (
          <div style={{ marginBottom: 12 }}>
            <button
              type="button"
              className="admin-btn"
              onClick={() => navigate('/admin/cleanup')}
              data-testid="admin-overview-open-cleanup"
            >
              <Trash2 size={14} />
              {isArabic ? 'أدوات تنظيف الإعلانات الوهمية' : 'Mock-ads cleanup tools'}
            </button>
          </div>
        )}

        <div
          className="admin-card"
          data-testid="admin-overview-error"
          style={{ borderInlineStart: '3px solid #f87171' }}
        >
          <h3 className="admin-card-title" style={{ color: '#f87171' }}>
            {isArabic ? 'تعذر تحميل النظرة العامة' : 'Could not load overview'}
          </h3>
          <ul style={{ margin: 0, paddingInlineStart: 18, fontSize: 13, lineHeight: 1.7 }}>
            <li>
              {isArabic ? 'نقطة النهاية' : 'Endpoint'}: <code>{error.endpoint}</code>
            </li>
            <li>
              {isArabic ? 'حالة HTTP' : 'HTTP status'}: <strong>{error.status}</strong>
              {error.status === 0 && (
                <span style={{ marginInlineStart: 6, color: '#94a3b8' }}>
                  ({isArabic ? 'خطأ شبكة' : 'network error'})
                </span>
              )}
            </li>
            <li>
              {isArabic ? 'التفاصيل' : 'Detail'}:{' '}
              <code style={{ wordBreak: 'break-word' }}>{error.message}</code>
            </li>
            <li style={{ marginTop: 4, color: '#94a3b8' }}>
              {isArabic
                ? 'استخدم زر التحديث أعلاه بعد التحقق من الشبكة / تسجيل الدخول.'
                : 'Use the Refresh button above after checking your network / login.'}
            </li>
          </ul>
          {error.actionable === 'login' && (
            <div style={{ marginTop: 12, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button
                type="button"
                className="admin-btn primary"
                onClick={() => {
                  try { localStorage.removeItem('token'); } catch (_e) {}
                  navigate('/auth');
                }}
                data-testid="admin-overview-error-login"
              >
                {isArabic ? 'تسجيل الدخول مرة أخرى' : 'Sign in again'}
              </button>
            </div>
          )}
          {error.actionable === 'no_admin' && (
            <div style={{ marginTop: 12, color: '#94a3b8', fontSize: 12 }}>
              {isArabic
                ? 'هذا الحساب ليس مسؤولاً. سجّل الدخول كحساب مسؤول.'
                : 'This account is not an admin. Sign in with an admin account.'}
            </div>
          )}
        </div>
      </div>
    );
  }

  if (!data) return null;

  const labels = isArabic ? STATUS_LABELS_AR : STATUS_LABELS_EN;
  const adsByStatus = data.ads?.by_status || {};
  const adsTotal = data.ads?.total || 0;
  const shopVisible = data.ads?.shop_visible || 0;
  // iter309 — `public_live` is the canonical live-shop-ads count.
  // It is identical to `shop_visible` (backend returns both for clarity);
  // we read `public_live` when available so the UI follows the new
  // contract name and falls back to `shop_visible` for older backends.
  const publicLiveAds = data.ads?.public_live ?? shopVisible;
  // iter289 — explicit diagnostic counts from backend.
  const adsLegacyRepair = data.ads?.legacy_repair_queue ?? 0;
  const adsDeletedExcluded = data.ads?.deleted_excluded ?? 0;
  const adsTestSeedExcluded = data.ads?.test_seed_owned_excluded ?? 0;
  const adsRawDbTotal = data.ads?.raw_db_total ?? 0;
  const booksByStatus = data.books?.by_status || {};
  const booksTotal = data.books?.total || 0;
  const booksShopVisible = data.books?.shop_visible || 0;
  const moderation = data.moderation || {};
  const realUsers = data.users?.real_users ?? data.users?.total ?? 0;
  const seedUsers = data.users?.seed_users ?? 0;
  const testUsers = data.users?.test_users ?? 0;
  // iter325 — Reports diagnostic block (only present on iter325+ backends).
  const reportsDiag = data.reports?.diagnostic || null;
  const reportsRawTotal = reportsDiag?.raw_total ?? data.reports?.total ?? 0;
  const reportsOrphanExcluded = reportsDiag?.orphan_excluded ?? 0;
  // iter325 — Users diagnostic helpers (already in payload from iter274).
  const usersExcludedTotal = data.users?.diagnostic?.excluded_total ?? 0;
  const usersInternalExcluded = data.users?.diagnostic?.excluded_by_bucket?.internal ?? 0;
  const usersDeletedExcluded = data.users?.diagnostic?.excluded_by_bucket?.deleted ?? 0;
  const usersRawTotal = data.users?.diagnostic?.raw_total ?? realUsers;
  const lastRefreshIso = data.generated_at || null;
  const lastRefreshHuman = lastRefreshIso
    ? new Date(lastRefreshIso).toLocaleTimeString(isArabic ? 'ar-SD' : 'en', { hour12: false })
    : '—';

  // iter325 — Small "Why this number?" popover. Tap the (?) chip next
  // to any KPI to read the query, included statuses, excluded
  // statuses, source endpoint, and refresh timestamp without opening
  // DevTools. The popover is single-instance (`openWhyKey`) so the
  // overview never has more than one open at a time.
  const whyOpenKey = openWhyKey;
  const renderWhyChip = (k, query, includes, excludes, source) => (
    <WhyChip
      k={k}
      query={query}
      includes={includes}
      excludes={excludes}
      source={source}
      isOpen={whyOpenKey === k}
      onToggle={() => setOpenWhyKey(whyOpenKey === k ? null : k)}
      isArabic={isArabic}
      lastRefreshHuman={lastRefreshHuman}
    />
  );

  return (
    <div data-testid="admin-overview">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700 }} data-testid="admin-overview-title">
            {isArabic ? 'نظرة عامة على المنصة' : 'Platform Overview'}
          </h1>
          <p style={{ margin: '4px 0 0', color: 'var(--admin-text-dim)', fontSize: 13 }}>
            {isArabic
              ? 'الأرقام الرئيسية تستثني السجلات التجريبية والبذرة والمحذوفة والنظام.'
              : 'Primary numbers exclude test, seed, deleted, and system records.'}
          </p>
          <CountsChip countsHealth={countsHealth} isArabic={isArabic} />
        </div>
        <button
          type="button"
          className="admin-btn"
          onClick={load}
          data-testid="admin-overview-refresh"
        >
          <RefreshCw size={14} />
          {isArabic ? 'تحديث' : 'Refresh'}
        </button>
      </div>

      {isOwner && (
        <div style={{ marginBottom: 12 }}>
          <button
            type="button"
            className="admin-btn"
            onClick={() => navigate('/admin/cleanup')}
            data-testid="admin-overview-open-cleanup"
            title={isArabic ? 'أدوات تنظيف للمالك فقط' : 'Owner-only cleanup tools'}
          >
            <Trash2 size={14} />
            {isArabic ? 'أدوات تنظيف الإعلانات الوهمية' : 'Mock-ads cleanup tools'}
          </button>
        </div>
      )}

      <div className="admin-grid" style={{ marginBottom: 16 }}>
        <button
          type="button"
          className="admin-card admin-kpi-card"
          data-testid="overview-kpi-users"
          onClick={goToUsers}
          aria-label={isArabic ? 'فتح قسم المستخدمين' : 'Open Users section'}
        >
          <h3 className="admin-card-title"><Users size={14} style={{ verticalAlign: 'middle', marginInlineEnd: 6 }} />{isArabic ? 'المستخدمون' : 'Users'}</h3>
          <div className="admin-stat">
            <span className="admin-stat-value" data-testid="kpi-users-total">
              {realUsers}
              {renderWhyChip(
                'users-real',
                'not seed/test/internal/deleted',
                ['real signed-up users'],
                ['seed_user_*', '@test.com', '@example.com', '@internal', 'is_deleted'],
                '/api/admin/dashboard/overview · users.total (== users.real_users)'
              )}
            </span>
            <span className="admin-stat-label" data-testid="kpi-users-real-label">
              {isArabic ? 'مستخدمون حقيقيون فقط' : 'Real users only'}
            </span>
            {/* iter325 — Removed the inline "Raw DB" + "Test/Seed"
                pills from the main breakdown — they live in the
                Data Health panel below now. Only the actionable
                "Real" count + new-user trend remains in primary view. */}
            <span className="admin-stat-label" style={{ marginTop: 6 }}>
              +{data.users?.new_24h ?? 0} {isArabic ? 'خلال 24 ساعة' : 'in 24h'} · +{data.users?.new_7d ?? 0} {isArabic ? 'خلال 7 أيام' : 'in 7d'} · +{data.users?.new_30d ?? 0} {isArabic ? 'خلال 30 يوم' : 'in 30d'}
            </span>
            <div style={{ display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
              <span
                className="admin-pill red admin-pill-link"
                data-testid="kpi-users-banned"
                role="link"
                tabIndex={0}
                onClick={(e) => { e.stopPropagation(); goToBanned(); }}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); goToBanned(); } }}
              >
                {isArabic ? 'محظور' : 'Banned'} {data.users?.banned ?? 0}
              </span>
              <span className="admin-pill yellow" data-testid="kpi-users-suspended">{isArabic ? 'موقوف' : 'Suspended'} {data.users?.suspended ?? 0}</span>
              <span className="admin-pill blue" data-testid="kpi-users-admins">{isArabic ? 'مشرفون' : 'Admins'} {data.users?.admins ?? 0}</span>
              {(moderation.pending_bans ?? 0) > 0 && (
                <span
                  className="admin-pill yellow admin-pill-link"
                  data-testid="kpi-users-pending-ban"
                  role="link"
                  tabIndex={0}
                  onClick={(e) => { e.stopPropagation(); goToPendingBan(); }}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); goToPendingBan(); } }}
                >
                  {isArabic ? 'بانتظار الحظر' : 'Pending ban'} {moderation.pending_bans}
                </span>
              )}
            </div>
            {data._source && (
              <span
                className="admin-source-hint"
                style={{ display: 'block', marginTop: 6, fontSize: 10, color: 'var(--admin-text-dim)' }}
                data-testid="kpi-users-source"
              >
                {isArabic ? 'المصدر' : 'source'}: <code className="admin-source-code">{data._source}</code>
              </span>
            )}
          </div>
        </button>

        <div
          role="button"
          tabIndex={0}
          className="admin-card admin-kpi-card"
          data-testid="overview-kpi-ads"
          onClick={goToShopAds}
          onKeyDown={(e) => {
            // Treat Enter / Space like a click so keyboard users
            // keep the same affordance the old <button> gave.
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              goToShopAds();
            }
          }}
          aria-label={isArabic ? 'فتح إعلانات المتجر المباشرة' : 'Open Live Shop Ads'}
          style={{ cursor: 'pointer' }}
        >
          <h3 className="admin-card-title"><ShoppingBag size={14} style={{ verticalAlign: 'middle', marginInlineEnd: 6 }} />{isArabic ? 'الإعلانات' : 'Ads'}</h3>
          <div className="admin-stat">
            {/* iter309 — Big headline = canonical public-live-ads count.
                Matches the Shop page "Browse Listings" number exactly. */}
            <span className="admin-stat-value" data-testid="kpi-ads-public-live">
              {publicLiveAds}
              {renderWhyChip(
                'live-ads',
                "status='active' AND visible to public AND owner not test/seed/internal",
                ['active'],
                ['pending_review', 'rejected', 'hidden', 'sold', 'deleted', 'needs_photo_reupload', 'test/seed/internal owners', 'CALIB_/TEST_ titles'],
                '/api/admin/dashboard/overview · ads.public_live (== /api/admin/stats/live-ads-debug)'
              )}
            </span>
            <span className="admin-stat-label" data-testid="kpi-ads-public-live-label">
              {isArabic ? 'إعلانات المتجر المباشرة' : 'Live Shop Ads'}
            </span>
            {/* iter325 — Primary action chips ONLY. Raw DB / Legacy
                Repair / Total Marketplace Records / Test-Seed
                Excluded have all been moved to the collapsible
                "Data Health / Debug" section at the bottom of this
                page so the operator sees only actionable production
                numbers here. */}
            <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }} data-testid="kpi-ads-breakdown">
              <span
                className="admin-pill yellow admin-pill-link"
                data-testid="kpi-ads-pending"
                role="link"
                tabIndex={0}
                onClick={(e) => { e.stopPropagation(); goToPendingAds(); }}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); goToPendingAds(); } }}
              >
                {isArabic ? 'بانتظار المراجعة' : 'Pending'}: <strong>{adsByStatus.pending_review ?? 0}</strong>
              </span>
              {renderWhyChip(
                'pending-ads',
                "status='pending_review' AND owner not test/seed",
                ['pending_review'],
                ['test/seed owners', 'all other statuses'],
                '/api/admin/dashboard/overview · ads.by_status.pending_review (== /api/admin/ads/pending total)'
              )}
              <span
                className="admin-pill red admin-pill-link"
                data-testid="kpi-ads-rejected"
                role="link"
                tabIndex={0}
                onClick={(e) => { e.stopPropagation(); goToRejectedAds(); }}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); goToRejectedAds(); } }}
              >
                {isArabic ? 'مرفوض' : 'Rejected'}: <strong>{adsByStatus.rejected ?? 0}</strong>
              </span>
            </div>
            <span style={{ display: 'block', marginTop: 6, fontSize: 10, color: 'var(--admin-text-dim)' }} data-testid="kpi-ads-exclusion-hint">
              {isArabic
                ? 'الرقم الرئيسي يطابق صفحة المتجر. الأرقام التشخيصية ضمن قسم سلامة البيانات بالأسفل.'
                : 'Headline matches Shop page. Diagnostic numbers live in the Data Health panel below.'}
            </span>

            {/* iter320 — Helper line + Sync-now action. The helper
                text is the contract: this number IS what Home and
                Shop render. The Sync button re-queries the
                canonical source with `cache: 'no-store'` so a
                stale CDN read on production can't lie about it. */}
            <div
              style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginTop: 6, flexWrap: 'wrap' }}
              data-testid="kpi-ads-sync-row"
            >
              <span
                style={{ fontSize: 11, color: 'var(--admin-text-dim)' }}
                data-testid="kpi-ads-shop-parity-hint"
              >
                {isArabic
                  ? 'يطابق الإعلانات الظاهرة في الصفحة الرئيسية والمتجر.'
                  : 'Matches ads visible on Home and Shop.'}
              </span>
              <button
                type="button"
                className="admin-btn"
                style={{ padding: '4px 10px', fontSize: 11, lineHeight: 1.2, marginInlineStart: 'auto' }}
                onClick={(e) => { e.stopPropagation(); syncNow(); }}
                disabled={syncing}
                data-testid="overview-sync-now"
                aria-label={isArabic ? 'تحديث إحصاءات الإعلانات' : 'Sync ad stats'}
              >
                <RefreshCw size={12} className={syncing ? 'animate-spin' : ''} style={{ marginInlineEnd: 4 }} />
                {syncing
                  ? (isArabic ? 'جارٍ التحديث…' : 'Syncing…')
                  : (isArabic ? 'تحديث الآن' : 'Sync now')}
              </button>
            </div>

            {/* iter320 — Mismatch diagnostic chip. Only renders when
                Sync was tapped AND the three counts diverge. Lists
                the symmetric-difference ad_ids so the operator can
                investigate without leaving the page. */}
            {debugSnapshot && mismatchFlag && (
              <div
                role="alert"
                data-testid="kpi-ads-mismatch-panel"
                style={{
                  marginTop: 8,
                  padding: 10,
                  borderRadius: 10,
                  background: 'rgba(245, 158, 11, 0.12)',
                  border: '1px solid rgba(245, 158, 11, 0.35)',
                  color: '#92400e',
                  fontSize: 11,
                  lineHeight: 1.5,
                  textAlign: isArabic ? 'right' : 'left',
                  width: '100%',
                  boxSizing: 'border-box',
                }}
              >
                <strong style={{ display: 'block', marginBottom: 4 }} data-testid="kpi-ads-mismatch-title">
                  {isArabic ? 'عدم تطابق في عدد الإعلانات' : 'Admin live ad count mismatch'}
                </strong>
                <div data-testid="kpi-ads-mismatch-counts">
                  Admin Live Shop Ads: <strong>{String(debugSnapshot.adminLiveCount)}</strong> ·
                  Canonical (debug): <strong>{String(debugSnapshot.canonicalCount)}</strong> ·
                  /api/ads.total: <strong>{String(debugSnapshot.publicAdsCount)}</strong>
                </div>
                {debugSnapshot.diff.inAOnly.length > 0 && (
                  <div data-testid="kpi-ads-mismatch-only-admin" style={{ marginTop: 4 }}>
                    {isArabic ? 'في عدّ الإدارة فقط:' : 'In admin canonical but NOT in /api/ads:'} {debugSnapshot.diff.inAOnly.slice(0, 8).join(', ')}{debugSnapshot.diff.inAOnly.length > 8 ? ` … (+${debugSnapshot.diff.inAOnly.length - 8})` : ''}
                  </div>
                )}
                {debugSnapshot.diff.inBOnly.length > 0 && (
                  <div data-testid="kpi-ads-mismatch-only-public" style={{ marginTop: 4 }}>
                    {isArabic ? 'في /api/ads فقط:' : 'In /api/ads but NOT in admin canonical:'} {debugSnapshot.diff.inBOnly.slice(0, 8).join(', ')}{debugSnapshot.diff.inBOnly.length > 8 ? ` … (+${debugSnapshot.diff.inBOnly.length - 8})` : ''}
                  </div>
                )}
                {debugSnapshot.serverTime && (
                  <div style={{ marginTop: 4, opacity: 0.7 }}>
                    {isArabic ? 'وقت الخادم' : 'server_time'}: <code>{debugSnapshot.serverTime}</code>
                  </div>
                )}
              </div>
            )}
            {debugSnapshot && !mismatchFlag && (
              <div
                data-testid="kpi-ads-sync-ok"
                style={{ marginTop: 6, fontSize: 11, color: 'var(--admin-text-dim)' }}
              >
                {isArabic ? '✓ تطابق محقَّق' : '✓ Counts match.'} ({debugSnapshot.adminLiveCount} = {debugSnapshot.canonicalCount} = {debugSnapshot.publicAdsCount})
              </div>
            )}

            {/* iter321 — Mobile-visible diagnostic panel.
                Always renders after Sync now is tapped (debugSnapshot exists),
                so the operator can read every count on a phone without
                opening DevTools. Layout collapses to a column on
                narrow viewports; no horizontal scroll. */}
            {debugSnapshot && (
              <div
                data-testid="kpi-ads-diagnostic-panel"
                style={{
                  marginTop: 10,
                  padding: 10,
                  borderRadius: 10,
                  background: 'rgba(15, 23, 42, 0.04)',
                  border: '1px solid rgba(15, 23, 42, 0.08)',
                  fontSize: 11,
                  lineHeight: 1.4,
                  textAlign: isArabic ? 'right' : 'left',
                  width: '100%',
                  boxSizing: 'border-box',
                }}
              >
                <strong
                  style={{ display: 'block', marginBottom: 6 }}
                  data-testid="kpi-ads-diagnostic-title"
                >
                  {isArabic ? 'لوحة تشخيص الأعداد (مرئية على الموبايل)' : 'Counts diagnostic (mobile-visible)'}
                </strong>

                {/* Count grid — two-column on phones, four-column on
                    desktop. Uses CSS grid + auto-fill so the values
                    wrap naturally on narrow widths. */}
                <div
                  data-testid="kpi-ads-counts-grid"
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
                    gap: 6,
                    margin: '6px 0',
                  }}
                >
                  <div data-testid="kpi-ads-count-home">
                    {isArabic ? 'مرئي في الصفحة الرئيسية' : 'Home visible'}: <strong>{String(debugSnapshot.publicAdsCount)}</strong>
                  </div>
                  <div data-testid="kpi-ads-count-shop">
                    {isArabic ? 'مرئي في المتجر' : 'Shop visible'}: <strong>{String(debugSnapshot.publicAdsCount)}</strong>
                  </div>
                  <div data-testid="kpi-ads-count-admin-live">
                    {isArabic ? 'إعلانات المتجر المباشرة (الإدارة)' : 'Admin Live Shop Ads'}: <strong>{String(debugSnapshot.adminLiveCount)}</strong>
                  </div>
                  <div data-testid="kpi-ads-count-canonical">
                    {isArabic ? 'العدد القانوني (التشخيص)' : 'Canonical (debug)'}: <strong>{String(debugSnapshot.canonicalCount)}</strong>
                  </div>
                  <div data-testid="kpi-ads-count-raw">
                    {isArabic ? 'إجمالي سجلات السوق' : 'Raw marketplace'}: <strong>{adsTotal}</strong>
                  </div>
                  <div data-testid="kpi-ads-count-pending">
                    {isArabic ? 'بانتظار المراجعة' : 'Pending'}: <strong>{adsByStatus.pending || 0}</strong>
                  </div>
                  <div data-testid="kpi-ads-count-rejected">
                    {isArabic ? 'مرفوضة' : 'Rejected'}: <strong>{adsByStatus.rejected || 0}</strong>
                  </div>
                  <div data-testid="kpi-ads-count-hidden">
                    {isArabic ? 'مخفية' : 'Hidden'}: <strong>{adsByStatus.hidden || 0}</strong>
                  </div>
                  <div data-testid="kpi-ads-count-deleted">
                    {isArabic ? 'محذوفة' : 'Deleted'}: <strong>{adsByStatus.deleted || 0}</strong>
                  </div>
                  <div data-testid="kpi-ads-count-expired">
                    {isArabic ? 'منتهية' : 'Expired'}: <strong>{adsByStatus.expired || 0}</strong>
                  </div>
                </div>

                {/* Per-ad mismatch table — only renders when there is
                    an actual diff. One card per mismatched ad with
                    every field the operator needs to triage the
                    drift right on the phone. */}
                {mismatchFlag && inspecting && (
                  <div data-testid="kpi-ads-mismatch-loading" style={{ marginTop: 8, opacity: 0.7 }}>
                    <Loader2 size={12} className="animate-spin" style={{ verticalAlign: 'middle', marginInlineEnd: 4 }} />
                    {isArabic ? 'جارٍ تحميل تفاصيل الإعلانات…' : 'Loading mismatched ad details…'}
                  </div>
                )}
                {mismatchFlag && !inspecting && Array.isArray(mismatchInspect) && mismatchInspect.length > 0 && (
                  <div
                    data-testid="kpi-ads-mismatch-detail"
                    style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6 }}
                  >
                    <strong style={{ fontSize: 11 }}>
                      {isArabic ? 'تفاصيل الإعلانات غير المتطابقة' : 'Mismatched ad details'} ({mismatchInspect.length})
                    </strong>
                    {mismatchInspect.map((item) => {
                      const inCanonNotPublic = debugSnapshot.diff.inAOnly.includes(item.ad_id);
                      const tone = inCanonNotPublic ? '#92400e' : '#1e40af';
                      const bg = inCanonNotPublic ? 'rgba(245, 158, 11, 0.12)' : 'rgba(59, 130, 246, 0.10)';
                      const border = inCanonNotPublic ? 'rgba(245, 158, 11, 0.35)' : 'rgba(59, 130, 246, 0.30)';
                      return (
                        <div
                          key={item.ad_id}
                          data-testid={`kpi-ads-mismatch-item-${item.ad_id}`}
                          style={{
                            padding: 8,
                            borderRadius: 8,
                            background: bg,
                            border: `1px solid ${border}`,
                            color: tone,
                            fontSize: 11,
                            lineHeight: 1.45,
                            wordBreak: 'break-word',
                          }}
                        >
                          <div style={{ fontWeight: 700, fontSize: 12, marginBottom: 2 }}>
                            <code>{item.ad_id}</code> · {item.title || (isArabic ? '— بدون عنوان —' : '— untitled —')}
                          </div>
                          <div>
                            {isArabic ? 'الحالة' : 'status'}: <strong>{String(item.status)}</strong>
                            {item.approval_status != null && (
                              <> · {isArabic ? 'الاعتماد' : 'approval'}: <strong>{String(item.approval_status)}</strong></>
                            )}
                          </div>
                          <div>
                            {isArabic ? 'مخفي' : 'hidden'}: <strong>{String(!!item.is_hidden)}</strong>
                            {' · '}
                            {isArabic ? 'محذوف' : 'deleted'}: <strong>{String(!!item.deleted)}</strong>
                            {' · '}
                            {isArabic ? 'منتهي' : 'expired'}: <strong>{String(!!item.expired)}</strong>
                            {item.availability_status && (
                              <> · {isArabic ? 'التوفر' : 'avail'}: <strong>{String(item.availability_status)}</strong></>
                            )}
                          </div>
                          <div style={{ opacity: 0.8 }}>
                            {isArabic ? 'المصدر' : 'source'}: <code>{item.source_collection || 'ads'}</code>
                            {' · '}
                            {isArabic ? 'في' : 'in'} {inCanonNotPublic
                              ? (isArabic ? 'العدّ القانوني فقط' : 'canonical only')
                              : (isArabic ? '/api/ads فقط' : '/api/ads only')}
                            {item.reason && <> · {item.reason}</>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
                {mismatchFlag && !inspecting && Array.isArray(mismatchInspect) && mismatchInspect.length === 0 && (
                  <div data-testid="kpi-ads-mismatch-empty" style={{ marginTop: 8, opacity: 0.7 }}>
                    {isArabic
                      ? 'لم نتمكن من جلب تفاصيل الإعلانات. حاول مرة أخرى.'
                      : "Couldn't load ad details. Try Sync now again."}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <button
          type="button"
          className="admin-card admin-kpi-card"
          data-testid="overview-kpi-books"
          onClick={goToBooks}
          aria-label={isArabic ? 'فتح قسم الكتب' : 'Open Books section'}
        >
          <h3 className="admin-card-title"><BookOpen size={14} style={{ verticalAlign: 'middle', marginInlineEnd: 6 }} />{isArabic ? 'الكتب' : 'Books'}</h3>
          <div className="admin-stat">
            <span className="admin-stat-value" data-testid="kpi-books-total">{booksTotal}</span>
            <span className="admin-stat-label">
              {isArabic ? 'قوائم الكتب التي ينشرها الطلاب' : 'Student-posted book listings'}
            </span>
            <div style={{ display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
              <span className="admin-pill green" data-testid="kpi-books-shop">{isArabic ? 'في المتجر' : 'Shop visible'} {booksShopVisible}</span>
              <span className="admin-pill yellow" data-testid="kpi-books-pending">{isArabic ? 'بانتظار المراجعة' : 'Pending'} {booksByStatus.pending_review ?? 0}</span>
              <span className="admin-pill red" data-testid="kpi-books-rejected">{isArabic ? 'مرفوض' : 'Rejected'} {booksByStatus.rejected ?? 0}</span>
            </div>
          </div>
        </button>

        <button
          type="button"
          className="admin-card admin-kpi-card"
          data-testid="overview-kpi-reports"
          onClick={goToReports}
          aria-label={isArabic ? 'فتح قسم البلاغات' : 'Open Reports section'}
        >
          <h3 className="admin-card-title"><AlertOctagon size={14} style={{ verticalAlign: 'middle', marginInlineEnd: 6 }} />{isArabic ? 'البلاغات' : 'Reports'}</h3>
          <div className="admin-stat">
            <span className="admin-stat-value" data-testid="kpi-reports-pending">
              {data.reports?.pending ?? 0}
              {renderWhyChip(
                'reports-pending',
                'status in {pending, none, missing} AND report_id exists',
                ['pending', 'unset status'],
                ['valid', 'invalid', 'dismissed', 'orphan rows (no report_id)'],
                '/api/admin/dashboard/overview · reports.pending (== /api/admin/reports?status=pending total)'
              )}
            </span>
            <span className="admin-stat-label">
              {isArabic ? 'بلاغات قيد المعالجة' : 'Pending reports'} · {isArabic ? 'الإجمالي' : 'total'} {data.reports?.total ?? 0}
            </span>
          </div>
        </button>

        <button
          type="button"
          className="admin-card admin-kpi-card"
          data-testid="overview-kpi-moderation"
          onClick={goToSystemHealth}
          aria-label={isArabic ? 'فتح صحة النظام' : 'Open system health'}
        >
          <h3 className="admin-card-title"><ShieldAlert size={14} style={{ verticalAlign: 'middle', marginInlineEnd: 6 }} />{isArabic ? 'الإشراف وصحة النظام' : 'Moderation & System Health'}</h3>
          <div className="admin-stat">
            <span className="admin-stat-value" data-testid="kpi-moderation-blocklist">
              {(moderation.blocklist_hard ?? 0) + (moderation.blocklist_soft ?? 0)}
            </span>
            <span className="admin-stat-label">
              {isArabic ? 'كلمات قائمة الحظر' : 'Blocklist terms'}
              {' · '}
              <strong data-testid="kpi-moderation-hard">{moderation.blocklist_hard ?? 0}</strong> hard
              {' / '}
              <strong data-testid="kpi-moderation-soft">{moderation.blocklist_soft ?? 0}</strong> soft
            </span>
            <div style={{ display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
              <span className="admin-pill blue admin-pill-link" role="link" tabIndex={0} onClick={(e) => { e.stopPropagation(); goToSystemHealth(); }} onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); goToSystemHealth(); } }}>
                <Activity size={11} style={{ verticalAlign: 'middle', marginInlineEnd: 3 }} />
                {isArabic ? 'صحة النظام' : 'System Health'}
              </span>
            </div>
          </div>
        </button>
      </div>

      <div className="admin-card" data-testid="overview-status-breakdown">
        <h3 className="admin-card-title">{isArabic ? 'تفصيل حالات الإعلانات (سوق حقيقي)' : 'Ads status breakdown (real marketplace)'}</h3>
        <p style={{ margin: '0 0 8px', color: 'var(--admin-text-dim)', fontSize: 12 }}>
          {isArabic
            ? 'يستثني السجلات التجريبية/البذرة/المحذوفة. لا يشمل طابور إصلاح الصور القديمة (انظر البطاقة أدناه).'
            : 'Excludes test/seed/deleted records. Does not include the Legacy Image Repair Queue (see card below).'}
        </p>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {Object.keys(adsByStatus)
            .filter((k) => k !== 'needs_photo_reupload')
            .map((key) => (
              <button
                key={key}
                type="button"
                className={`admin-pill admin-pill-link ${STATUS_PILL[key] || 'gray'}`}
                data-testid={`overview-status-${key}`}
                onClick={() => goToAdsByStatus(key)}
                aria-label={`${labels[key] || key}: ${adsByStatus[key]}`}
              >
                {labels[key] || key}: {adsByStatus[key]}
              </button>
            ))}
        </div>
      </div>

      {/* iter289 — Legacy Image Repair Queue as its own card. NOT
          counted in the marketplace total. Shows every legacy item
          regardless of user (including test/seed) since the operator
          needs to recover all of them. */}
      {adsLegacyRepair > 0 && (
        <div className="admin-card" style={{ marginTop: 16 }} data-testid="overview-legacy-repair">
          <h3 className="admin-card-title">
            {isArabic ? 'طابور إصلاح الصور القديمة' : 'Legacy Image Repair Queue'}
          </h3>
          <p style={{ margin: '0 0 8px', color: 'var(--admin-text-dim)', fontSize: 12 }}>
            {isArabic
              ? 'إعلانات قديمة فقدت ملفات الصور قبل ترحيل Cloudinary. لا يُحتسب ضمن إجمالي إعلانات السوق.'
              : 'Legacy ads whose image files were lost before the Cloudinary migration. NOT included in the marketplace total.'}
          </p>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <span className="admin-stat-value" data-testid="overview-legacy-repair-count">{adsLegacyRepair}</span>
            <button
              type="button"
              className="admin-btn"
              onClick={() => navigate('/admin/ads?status=needs_photo_reupload')}
              data-testid="overview-legacy-repair-browse"
            >
              {isArabic ? 'استعراض الطابور' : 'Browse Queue'}
            </button>
          </div>
        </div>
      )}

      <div className="admin-card" style={{ marginTop: 16 }} data-testid="overview-books-breakdown">
        <h3 className="admin-card-title">{isArabic ? 'تفصيل حالات الكتب' : 'Books status breakdown'}</h3>
        {booksTotal === 0 ? (
          <p style={{ color: 'var(--admin-text-dim)', fontSize: 13, margin: 0 }} data-testid="overview-books-empty">
            {isArabic ? 'لا توجد قوائم كتب بعد.' : 'No book listings yet.'}
          </p>
        ) : (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {Object.keys(booksByStatus).map((key) => (
              <button
                key={key}
                type="button"
                className={`admin-pill admin-pill-link ${STATUS_PILL[key] || 'gray'}`}
                data-testid={`overview-books-status-${key}`}
                onClick={() => goToBooksByStatus(key)}
                aria-label={`${labels[key] || key}: ${booksByStatus[key]}`}
              >
                {labels[key] || key}: {booksByStatus[key]}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="admin-card" style={{ marginTop: 16 }} data-testid="overview-recent-audit">
        <h3 className="admin-card-title"><ScrollText size={14} style={{ verticalAlign: 'middle', marginInlineEnd: 6 }} />{isArabic ? 'آخر إجراءات المشرفين' : 'Recent admin actions'}</h3>
        {(!data.recent_audit || data.recent_audit.length === 0) ? (
          <p style={{ color: 'var(--admin-text-dim)', fontSize: 13, margin: 0 }}>
            {isArabic ? 'لا توجد إجراءات بعد.' : 'No admin actions logged yet.'}
          </p>
        ) : (
          <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {data.recent_audit.map((entry, idx) => (
              <li key={idx} style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13 }} data-testid={`overview-audit-row-${idx}`}>
                <span className="admin-pill blue">{entry.action}</span>
                <span style={{ color: 'var(--admin-text-dim)' }}>
                  {entry.target_type}:{entry.target_id || '—'}
                </span>
                <span style={{ marginInlineStart: 'auto', color: 'var(--admin-text-dim)' }}>
                  {entry.timestamp ? new Date(entry.timestamp).toLocaleString() : ''}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* iter325 — Data Health / Debug panel. Collapsed by default. */}
      <div
        className="admin-card"
        style={{ marginTop: 16, borderColor: 'var(--admin-border-dim, #e5e7eb)' }}
        data-testid="overview-data-health"
      >
        <button
          type="button"
          onClick={() => setDebugOpen((v) => !v)}
          aria-expanded={debugOpen}
          data-testid="data-health-toggle"
          style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            width: '100%', background: 'none', border: 'none', padding: 0,
            cursor: 'pointer', textAlign: isArabic ? 'right' : 'left',
          }}
        >
          <h3 className="admin-card-title" style={{ margin: 0 }}>
            <Activity size={14} style={{ verticalAlign: 'middle', marginInlineEnd: 6 }} />
            {isArabic
              ? 'سلامة البيانات / تشخيصات — ليست جزءاً من أرقام السوق المباشرة'
              : 'Data Health / Debug — not part of live marketplace counts'}
          </h3>
          <span style={{ fontSize: 13, color: 'var(--admin-text-dim)' }} aria-hidden="true">
            {debugOpen ? '▼' : '▶'}
          </span>
        </button>
        {debugOpen && (
          <div style={{ marginTop: 12 }} data-testid="data-health-body">
            <p style={{ fontSize: 12, color: 'var(--admin-text-dim)', margin: '0 0 12px' }}>
              {isArabic
                ? 'الأرقام أدناه للتشخيص فقط. لا تستخدم في القرارات التشغيلية ولا تظهر للمستخدمين.'
                : 'Numbers below are diagnostic only. They are not used in operational decisions and are never shown to end users.'}
            </p>

            {/* Ads diagnostics */}
            <div style={{ marginBottom: 16 }} data-testid="data-health-ads">
              <h4 style={{ fontSize: 13, fontWeight: 700, margin: '0 0 6px' }}>
                {isArabic ? 'إعلانات — تشخيصي' : 'Ads — diagnostic'}
              </h4>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                <span className="admin-pill green" data-testid="dh-ads-real-marketplace">
                  {isArabic ? 'إجمالي سجلات السوق الحقيقية' : 'Total real marketplace records'}: <strong>{adsTotal}</strong>
                </span>
                <span className="admin-pill gray" data-testid="dh-ads-deleted">
                  {isArabic ? 'محذوف' : 'Deleted'}: <strong>{adsDeletedExcluded}</strong>
                </span>
                {adsLegacyRepair > 0 && (
                  <span className="admin-pill yellow" data-testid="dh-ads-legacy-repair">
                    {isArabic ? 'إصلاح الصور القديمة' : 'Legacy Repair'}: <strong>{adsLegacyRepair}</strong>
                  </span>
                )}
                <span className="admin-pill gray" data-testid="dh-ads-test-seed">
                  {isArabic ? 'اختبار/بذرة (مستثناة)' : 'Test/Seed (excluded)'}: <strong>{adsTestSeedExcluded}</strong>
                </span>
                <span className="admin-pill blue" data-testid="dh-ads-raw-db">
                  {isArabic ? 'الكل (خام)' : 'Raw DB'}: <strong>{adsRawDbTotal}</strong>
                </span>
              </div>
            </div>

            {/* Users diagnostics */}
            <div style={{ marginBottom: 16 }} data-testid="data-health-users">
              <h4 style={{ fontSize: 13, fontWeight: 700, margin: '0 0 6px' }}>
                {isArabic ? 'مستخدمون — تشخيصي' : 'Users — diagnostic'}
              </h4>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                <span className="admin-pill green" data-testid="dh-users-real">
                  {isArabic ? 'حقيقي' : 'Real'}: <strong>{realUsers}</strong>
                </span>
                <span className="admin-pill gray" data-testid="dh-users-test-seed">
                  {isArabic ? 'اختبار/بذرة' : 'Test/Seed'}: <strong>{testUsers + seedUsers}</strong>
                </span>
                <span className="admin-pill gray" data-testid="dh-users-internal">
                  {isArabic ? 'داخلي' : 'Internal'}: <strong>{usersInternalExcluded}</strong>
                </span>
                <span className="admin-pill gray" data-testid="dh-users-deleted">
                  {isArabic ? 'محذوف' : 'Deleted'}: <strong>{usersDeletedExcluded}</strong>
                </span>
                <span className="admin-pill blue" data-testid="dh-users-raw">
                  {isArabic ? 'الكل (خام)' : 'Raw DB'}: <strong>{usersRawTotal}</strong>
                </span>
              </div>
              <p style={{ fontSize: 11, color: 'var(--admin-text-dim)', margin: '6px 0 0' }}>
                {isArabic
                  ? `إجمالي المستثناة: ${usersExcludedTotal} (${usersRawTotal} - ${realUsers} = ${usersRawTotal - realUsers})`
                  : `Excluded total: ${usersExcludedTotal} (${usersRawTotal} - ${realUsers} = ${usersRawTotal - realUsers})`}
              </p>
            </div>

            {/* Reports diagnostics */}
            <div style={{ marginBottom: 16 }} data-testid="data-health-reports">
              <h4 style={{ fontSize: 13, fontWeight: 700, margin: '0 0 6px' }}>
                {isArabic ? 'بلاغات — تشخيصي' : 'Reports — diagnostic'}
              </h4>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                <span className="admin-pill green" data-testid="dh-reports-actionable">
                  {isArabic ? 'قابل للمعالجة' : 'Actionable'}: <strong>{data.reports?.total ?? 0}</strong>
                </span>
                <span className="admin-pill gray" data-testid="dh-reports-orphan">
                  {isArabic ? 'يتيم/تالف' : 'Orphan/malformed'}: <strong>{reportsOrphanExcluded}</strong>
                </span>
                <span className="admin-pill blue" data-testid="dh-reports-raw">
                  {isArabic ? 'الكل (خام)' : 'Raw DB'}: <strong>{reportsRawTotal}</strong>
                </span>
              </div>
            </div>

            {/* Counts verification details */}
            {countsHealth && (
              <div data-testid="data-health-verify">
                <h4 style={{ fontSize: 13, fontWeight: 700, margin: '0 0 6px' }}>
                  {isArabic ? 'فحوصات التطابق' : 'Parity checks'}
                </h4>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12 }}>
                  {Object.entries(countsHealth.checks || {}).map(([k, c]) => (
                    <li key={k} style={{ display: 'flex', gap: 6, alignItems: 'center' }} data-testid={`dh-check-${k}`}>
                      <span aria-hidden="true">{c.match ? '✓' : '⚠'}</span>
                      <span><strong>{c.label}</strong>: {c.expected} vs {c.got}</span>
                    </li>
                  ))}
                </ul>
                <p style={{ fontSize: 11, color: 'var(--admin-text-dim)', margin: '6px 0 0' }}>
                  {isArabic ? 'آخر فحص:' : 'Last verified:'} {countsHealth.generated_at ? new Date(countsHealth.generated_at).toLocaleString() : '—'}
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
